// =============================================================
// DamConnectorModal.jsx — connect part of the library to a Claude Project.
//
// Mints a remote MCP connector: the user copies its URL and pastes it into a
// claude.ai Project as a Custom Connector, after which Claude can search/read
// those assets live. Mirrors DamShareModal (create form + live status +
// per-connector call log + revoke/delete).
//
// THE SCOPE SELECT IS THE POINT OF THIS DIALOG'S LAST REVISION. A connector
// used to be one collection and nothing else, so an asset in the library's
// Uncollected view was unreachable through Claude no matter what was searched —
// every read is `CollectionID IN (…)`, which cannot match the NULL an
// uncollected asset carries. "Assets not in a collection" and "Entire asset
// library" are the two scopes that were missing. The scope is stated in words
// on every row, because a connector's whole behaviour follows from it and an
// empty search result is otherwise indistinguishable from an empty library.
//
// `supportsScope: false` (a database behind migration 187) hides the wider
// options rather than offering a control whose save the API would refuse.
//
// Admin-only (the backend mint/list endpoints require the Admin role — a
// connector exposes a whole collection or more). Part of the removable DAM module.
// =============================================================
import { useState, useEffect, useCallback } from 'react';
import { B, space, font, radius, weight } from '../../constants/brand';
import { Modal, Btn, Input, Select, Badge, Icon, Spinner, useToast } from '../../components/ui';
import { createConnector, listConnectors, connectorEvents, revokeConnector, deleteConnector } from './damApi';
import { formatInstant } from '../../utils/format';

const EXPIRY_OPTIONS = [
  { value: '',    label: 'Never expires' },
  { value: '30',  label: 'Expires in 30 days' },
  { value: '90',  label: 'Expires in 90 days' },
  { value: '365', label: 'Expires in 1 year' },
];

const STATUS_COLOR = { active: B.green, expired: B.textMd, revoked: B.red, exhausted: B.amber };

// Mirrors SCOPES in api/src/damMcpScope.js. 'collection' is first because it is
// the narrowest and the default — a connector hands read access to whoever
// holds its URL, so the widest option must be chosen, never landed on.
const SCOPE_OPTIONS = [
  { value: 'collection',  label: 'One collection' },
  { value: 'uncollected', label: 'Assets not in a collection' },
  { value: 'all',         label: 'Entire asset library' },
];

// What each scope lets Claude read, in the words the dialog shows. Kept short
// enough to sit under the select where the choice is actually made.
const SCOPE_HELP = {
  collection: 'Claude sees only the collection you pick. It will not find assets in other collections, or assets that are not filed in a collection at all.',
  uncollected: 'Claude sees every asset that has not been filed into a collection (the library’s “Uncollected” view). It will not see assets inside a collection.',
  all: 'Claude sees every asset in the library, collected or not. The broadest option — anyone with this URL can read the whole library.',
};

export function DamConnectorModal({ collections = [], defaultCollectionId = '', onClose }) {
  const { toast } = useToast();

  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needsInit, setNeedsInit] = useState(false);
  const [creating, setCreating] = useState(false);
  // Whether this database can store a wider scope (migration 187). Assume it
  // can until the list says otherwise, then hide what cannot be saved.
  const [supportsScope, setSupportsScope] = useState(true);

  // Create-form state
  const [scope, setScope] = useState('collection');
  const [collectionId, setCollectionId] = useState(defaultCollectionId || (collections[0]?.collectionID ?? ''));
  const [name, setName] = useState('');
  const [includeSubcollections, setIncludeSubcollections] = useState(false);
  const [allowDownload, setAllowDownload] = useState(true);
  const [expiresInDays, setExpiresInDays] = useState('');
  const [maxCalls, setMaxCalls] = useState('');

  // Lists EVERY connector, deliberately unfiltered: a whole-library or
  // uncollected connector belongs to no collection, so filtering by the create
  // form's collection would hide exactly the connectors this dialog now mints.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listConnectors();
      if (res.needsInit) { setNeedsInit(true); setConnectors([]); return; }
      setNeedsInit(false);
      setConnectors(res.connectors || []);
      if (res.supportsScope === false) { setSupportsScope(false); setScope('collection'); }
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success('MCP URL copied to clipboard'); }
    catch { toast.error('Could not copy — long-press / right-click the URL to copy it.'); }
  };

  const create = async () => {
    const needsCollection = scope === 'collection';
    if (needsCollection && !collectionId) { toast.error('Pick a collection to connect.'); return; }
    setCreating(true);
    try {
      const res = await createConnector({
        scope,
        // A wider scope belongs to no collection — send neither the id nor the
        // subtree flag, so the stored row cannot imply a collection it ignores.
        collectionId: needsCollection ? collectionId : null,
        name: name.trim() || null,
        includeSubcollections: needsCollection ? includeSubcollections : false,
        allowDownload,
        expiresInDays: expiresInDays ? Number(expiresInDays) : null,
        maxCalls: maxCalls ? Number(maxCalls) : null,
      });
      toast.success('Connector created');
      if (res.mcpUrl) await copy(res.mcpUrl);
      setName('');
      await load();
    } catch (e) {
      toast.error(e.status === 400 && e.data?.needsInit ? 'An admin must initialize the module first.' : e.message);
    } finally { setCreating(false); }
  };

  const revoke = async (c) => {
    if (!window.confirm('Turn off this connector? The connected Claude Project will lose access immediately.')) return;
    try { await revokeConnector(c.connectorID); toast.success('Connector turned off'); await load(); }
    catch (e) { toast.error(e.message); }
  };

  const remove = async (c) => {
    if (!window.confirm('Delete this connector and its call log?')) return;
    try { await deleteConnector(c.connectorID); await load(); }
    catch (e) { toast.error(e.message); }
  };

  const collectionOptions = collections.map(c => ({ value: c.collectionID, label: c.name }));

  return (
    <Modal title="Connect to Claude" onClose={onClose} width={720}>
      <div style={{ padding: space.xl, display: 'flex', flexDirection: 'column', gap: space.xl }}>
        {/* Intro */}
        <div style={{ fontSize: font.sm, color: B.textMd, lineHeight: 1.6 }}>
          Create a connector, then paste its URL into a <strong style={{ color: B.text }}>Claude Project</strong> as
          a <strong style={{ color: B.text }}>Custom Connector</strong> (Project → Settings → Connectors → Add custom connector).
          Claude can then search and read those assets live. <strong style={{ color: B.text }}>Claude sees only what the
          connector's scope covers</strong> — outside it, a search comes back empty even for a file you can see in this library.
          Anyone with the URL can read everything in scope, so keep it private and revoke it when you're done.
        </div>

        {needsInit ? (
          <div style={{ padding: space.lg, background: B.surfaceMuted, borderRadius: radius.lg, color: B.textMd, fontSize: font.sm }}>
            Connectors aren't set up yet. An admin can open <strong>Digital Assets</strong> and click <strong>Initialize module</strong> (or
            run migration&nbsp;145), then reopen this dialog.
          </div>
        ) : (
          <>
            {/* Create form */}
            <div style={{ border: `1px solid ${B.border}`, borderRadius: radius.xxl, padding: space.lg }}>
              <div style={{ fontSize: font.sm, fontWeight: weight.bold, color: B.text, marginBottom: space.md }}>New connector</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.md }}>
                <Select label="What Claude can see" value={scope} onChange={setScope}
                  options={supportsScope ? SCOPE_OPTIONS : SCOPE_OPTIONS.slice(0, 1)} />
                <Input label="Name (optional)" value={name} onChange={setName} placeholder="e.g. Marketing images for Claude" />
              </div>

              <div style={{ fontSize: font.xs, color: B.textMd, lineHeight: 1.5, marginTop: -space.xs, marginBottom: space.md }}>
                {SCOPE_HELP[scope]}
                {!supportsScope && (
                  <span style={{ display: 'block', marginTop: 4, color: B.amber }}>
                    Wider scopes need migration&nbsp;187 — an admin can run <strong>Initialize module</strong> on this page.
                  </span>
                )}
              </div>

              {scope === 'collection' && (
                <div style={{ marginBottom: space.md }}>
                  <Select label="Collection" value={collectionId} onChange={setCollectionId}
                    options={collectionOptions.length ? collectionOptions : [{ value: '', label: 'No collections yet' }]} />
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.md }}>
                <Select label="Expiry" value={expiresInDays} onChange={setExpiresInDays} options={EXPIRY_OPTIONS} />
                <Input label="Max tool calls (optional)" value={maxCalls} onChange={setMaxCalls} type="number" placeholder="Unlimited" />
              </div>

              {scope === 'collection' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: space.sm, cursor: 'pointer', fontSize: font.sm, color: B.text, marginTop: space.sm }}>
                  <input type="checkbox" checked={includeSubcollections} onChange={e => setIncludeSubcollections(e.target.checked)} style={{ width: 16, height: 16, accentColor: B.navy }} />
                  Include sub-collections (expose the whole folder tree)
                </label>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: space.sm, cursor: 'pointer', fontSize: font.sm, color: B.text, marginTop: space.sm, marginBottom: space.md }}>
                <input type="checkbox" checked={allowDownload} onChange={e => setAllowDownload(e.target.checked)} style={{ width: 16, height: 16, accentColor: B.navy }} />
                Let Claude view images &amp; get download links (uncheck for metadata-only)
              </label>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Btn onClick={create} loading={creating}><Icon name="add" size={14} /> Create connector</Btn>
              </div>
            </div>

            {/* Existing connectors */}
            <div>
              <div style={{ fontSize: font.sm, fontWeight: weight.bold, color: B.text, marginBottom: space.sm }}>
                Connectors {connectors.length > 0 && <span style={{ color: B.textLt }}>({connectors.length})</span>}
              </div>
              {loading ? <Spinner /> : connectors.length === 0 ? (
                <div style={{ padding: space.lg, color: B.textLt, fontSize: font.sm, textAlign: 'center' }}>No connectors yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
                  {connectors.map(c => <ConnectorRow key={c.connectorID} connector={c} onCopy={copy} onRevoke={revoke} onRemove={remove} />)}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function ConnectorRow({ connector, onCopy, onRevoke, onRemove }) {
  const { toast } = useToast();
  const [showLog, setShowLog] = useState(false);
  const [events, setEvents] = useState(null);
  const isActive = connector.status === 'active';

  const toggleLog = async () => {
    const next = !showLog;
    setShowLog(next);
    if (next && events === null) {
      try { const res = await connectorEvents(connector.connectorID); setEvents(res.events || []); }
      catch (e) { toast.error(e.message); setEvents([]); }
    }
  };

  const callsLabel = connector.maxCalls ? `${connector.callCount}/${connector.maxCalls}` : `${connector.callCount}`;

  return (
    <div style={{ border: `1px solid ${B.border}`, borderRadius: radius.lg, padding: '10px 12px', background: B.surface }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: font.base, fontWeight: weight.semibold, color: B.text }}>
            {connector.name || connector.collectionName || 'Connector'}
          </div>
          {/* The scope, in the server's own words — every tool result says the
              same thing, so what Claude reports and what this row says agree. */}
          <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 2 }}>
            Reads {connector.scopeLabel || (connector.collectionName ? `the "${connector.collectionName}" collection` : 'this connector’s collection')}
          </div>
          <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 2 }}>
            {callsLabel} call{connector.callCount === 1 ? '' : 's'}
            {connector.allowDownload ? '' : ' · metadata only'}
            {connector.expiresUTC ? ` · expires ${formatInstant(connector.expiresUTC, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
          </div>
        </div>
        <Badge color={STATUS_COLOR[connector.status] || B.textMd}>{connector.status}</Badge>
        <Btn size="sm" variant="ghost" onClick={() => onCopy(connector.mcpUrl)} disabled={!isActive}>
          <Icon name="attach" size={13} /> Copy URL
        </Btn>
        <button onClick={toggleLog} title="Call log" style={iconBtn}>
          <Icon name="list" size={15} color={B.textMd} />
        </button>
        {isActive && (
          <button onClick={() => onRevoke(connector)} title="Turn off connector" style={iconBtn}>
            <Icon name="lock" size={15} color={B.amber} />
          </button>
        )}
        <button onClick={() => onRemove(connector)} title="Delete" style={iconBtn}>
          <Icon name="trash" size={15} color={B.red} />
        </button>
      </div>

      {showLog && (
        <div style={{ marginTop: space.sm, paddingTop: space.sm, borderTop: `1px solid ${B.divider}` }}>
          {events === null ? <div style={{ fontSize: font.xs, color: B.textLt }}>Loading activity…</div>
            : events.length === 0 ? <div style={{ fontSize: font.xs, color: B.textLt }}>No activity yet.</div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 160, overflowY: 'auto' }}>
                {events.map(ev => (
                  <div key={ev.eventID} style={{ display: 'flex', gap: space.sm, fontSize: font.xs, color: B.textMd }}>
                    <span style={{ width: 120, fontWeight: weight.semibold, color: B.text }}>{ev.tool || '—'}</span>
                    <span style={{ width: 130, color: B.textLt }}>{formatInstant(ev.createdUTC)}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.detail || ''}{ev.ipAddress ? ` · ${ev.ipAddress}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}

const iconBtn = {
  border: 'none', background: 'transparent', cursor: 'pointer', padding: 6, borderRadius: radius.md,
  display: 'flex', alignItems: 'center',
};
